import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as PointerEventReact, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '@/shared/i18n/useT';

interface RGB { r: number; g: number; b: number }
interface HSV { h: number; s: number; v: number }

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

function hexToRgb(hex: string): RGB {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const num = parseInt(full, 16);
  if (full.length !== 6 || Number.isNaN(num)) return { r: 0, g: 0, b: 0 };
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function rgbToHex({ r, g, b }: RGB): string {
  const part = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`.toUpperCase();
}

function rgbToHsv({ r, g, b }: RGB): HSV {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn), d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = 60 * (((gn - bn) / d) % 6);
    else if (max === gn) h = 60 * ((bn - rn) / d + 2);
    else h = 60 * ((rn - gn) / d + 4);
  }
  if (h < 0) h += 360;
  return { h, s: max === 0 ? 0 : (d / max) * 100, v: max * 100 };
}

function hsvToRgb({ h, s, v }: HSV): RGB {
  const sn = s / 100, vn = v / 100;
  const c = vn * sn;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = vn - c;
  let rp = 0, gp = 0, bp = 0;
  if (h < 60) { rp = c; gp = x; bp = 0; }
  else if (h < 120) { rp = x; gp = c; bp = 0; }
  else if (h < 180) { rp = 0; gp = c; bp = x; }
  else if (h < 240) { rp = 0; gp = x; bp = c; }
  else if (h < 300) { rp = x; gp = 0; bp = c; }
  else { rp = c; gp = 0; bp = x; }
  return { r: (rp + m) * 255, g: (gp + m) * 255, b: (bp + m) * 255 };
}

const PALETTE: string[] = [
  '#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF',
  '#980000', '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#4A86E8', '#0000FF', '#9900FF', '#FF00FF',
  '#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC', '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC',
  '#DD7E6B', '#EA9999', '#F9CB9C', '#FFE599', '#B6D7A8', '#A2C4C9', '#A4C2F4', '#9FC5E8', '#B4A7D6', '#D5A6BD',
  '#CC4125', '#E06666', '#F6B26B', '#FFD966', '#93C47D', '#76A5AF', '#6D9EEB', '#6FA8DC', '#8E7CC3', '#C27BA0',
  '#A61C00', '#CC0000', '#E69138', '#F1C232', '#6AA84F', '#45818E', '#3C78D8', '#3D85C6', '#674EA7', '#A64D79',
  '#85200C', '#990000', '#B45F06', '#BF9000', '#38761D', '#134F5C', '#1155CC', '#0B5394', '#351C75', '#741B47',
  '#5B0F00', '#660000', '#783F04', '#7F6000', '#274E13', '#0C343D', '#1C4587', '#073763', '#20124D', '#4C1130',
];

export interface GridFillControls {
  scope: 'cell' | 'column' | 'header';
  onScopeChange: (scope: 'cell' | 'column' | 'header') => void;
  onResetColumn: () => void;
  resetColumnDisabled?: boolean;
}

interface ColorPickerProps {
  valor: string | null;
  onChange: (color: string | null) => void;
  trigger: ReactNode;
  coloresUsados?: string[];
  disabled?: boolean;
  triggerClassName?: string;
  ariaLabel?: string;
  title?: string;
  /** Optional fill controls for Grid; other color pickers retain their existing UI. */
  gridFill?: GridFillControls;
}

export function ColorPicker({ valor, onChange, trigger, coloresUsados, disabled, triggerClassName, ariaLabel, title, gridFill }: ColorPickerProps) {
  const { t } = useI18n();
  const [abierto, setAbierto] = useState(false);
  const [personalizando, setPersonalizando] = useState(false);
  const [hex, setHex] = useState(valor ?? '#000000');
  const [errorPegar, setErrorPegar] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ left: 8, top: 8, bottom: null as number | null });
  const contenedorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const cuadradoRef = useRef<HTMLDivElement>(null);
  const barraRef = useRef<HTMLDivElement>(null);

  useEffect(() => setHex(valor ?? '#000000'), [valor]);

  useEffect(() => {
    if (!abierto) return;
    const onClickFuera = (e: MouseEvent) => {
      const destino = e.target as Node;
      if (contenedorRef.current && !contenedorRef.current.contains(destino) && !panelRef.current?.contains(destino)) {
        setAbierto(false);
        setPersonalizando(false);
        setErrorPegar(false);
      }
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  const elegir = (color: string | null) => {
    onChange(color);
    setAbierto(false);
    setPersonalizando(false);
  };

  const copiar = () => {
    if (valor) void navigator.clipboard.writeText(valor);
  };

  const pegar = async () => {
    let texto = '';
    try { texto = (await navigator.clipboard.readText()).trim(); } catch { /* ignore */ }
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(texto)) onChange(texto);
    else setErrorPegar(true);
  };

  useEffect(() => {
    if (!errorPegar) return;
    const timer = setTimeout(() => setErrorPegar(false), 2500);
    return () => clearTimeout(timer);
  }, [errorPegar]);

  const rgbActual = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex) ? hexToRgb(hex) : { r: 0, g: 0, b: 0 };
  const hsvActual = rgbToHsv(rgbActual);
  const colorPuro = rgbToHex(hsvToRgb({ h: hsvActual.h, s: 100, v: hsvActual.v }));

  const aplicarHsv = (cambios: Partial<HSV>) => setHex(rgbToHex(hsvToRgb({ ...hsvActual, ...cambios })));
  const aplicarRgb = (cambios: Partial<RGB>) => setHex(rgbToHex({ ...rgbActual, ...cambios }));

  const arrastrar = (ref: typeof cuadradoRef, aplicar: (clientX: number, clientY: number, rect: DOMRect) => void) => (e: PointerEventReact<HTMLDivElement>) => {
    const mover = (clientX: number, clientY: number) => {
      if (!ref.current) return;
      aplicar(clientX, clientY, ref.current.getBoundingClientRect());
    };
    mover(e.clientX, e.clientY);
    const onMove = (ev: PointerEvent) => mover(ev.clientX, ev.clientY);
    const onUp = () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const onCuadradoPointer = arrastrar(cuadradoRef, (clientX, clientY, rect) => {
    const x = clamp(clientX - rect.left, 0, rect.width);
    const y = clamp(clientY - rect.top, 0, rect.height);
    aplicarHsv({ h: (x / rect.width) * 360, s: 100, v: 100 - (y / rect.height) * 100 });
  });

  const onBarraPointer = arrastrar(barraRef, (_clientX, clientY, rect) => {
    const y = clamp(clientY - rect.top, 0, rect.height);
    aplicarHsv({ s: (y / rect.height) * 100 });
  });

  const usados = [...new Set((coloresUsados ?? []).map((c) => c.toUpperCase()))];

  useLayoutEffect(() => {
    if (!abierto || !contenedorRef.current || !panelRef.current) return;
    const actualizarPosicion = () => {
      if (!contenedorRef.current || !panelRef.current) return;
      const rectBoton = contenedorRef.current.getBoundingClientRect();
      const anchoPanel = panelRef.current.offsetWidth;
      const altoPanel = panelRef.current.offsetHeight;
      const margen = 8;
      const espacioAbajo = window.innerHeight - rectBoton.bottom - margen;
      const espacioArriba = rectBoton.top - margen;
      const haciaArriba = espacioAbajo < altoPanel && espacioArriba > espacioAbajo;
      const left = Math.min(Math.max(rectBoton.left, margen), Math.max(margen, window.innerWidth - anchoPanel - margen));
      setPanelPosition(haciaArriba
        ? { left, top: 0, bottom: window.innerHeight - rectBoton.top + 4 }
        : { left, top: rectBoton.bottom + 4, bottom: null });
    };
    actualizarPosicion();
    window.addEventListener('resize', actualizarPosicion);
    window.addEventListener('scroll', actualizarPosicion, true);
    return () => {
      window.removeEventListener('resize', actualizarPosicion);
      window.removeEventListener('scroll', actualizarPosicion, true);
    };
  }, [abierto, personalizando, usados.length]);

  return (
    <div ref={contenedorRef} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        title={title}
        className={triggerClassName}
        onClick={() => setAbierto((a) => !a)}
      >
        {trigger}
      </button>
      {/* Portaled so a parent's stacking context (side panels) can't put the grid toolbar above it */}
      {abierto && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            left: panelPosition.left,
            ...(panelPosition.bottom !== null ? { bottom: panelPosition.bottom } : { top: panelPosition.top }),
          }}
          className="z-50 w-64 max-w-[calc(100vw-1rem)] overflow-hidden rounded-2xl border border-border bg-surface p-3 shadow-lg"
        >
          {!personalizando ? (
            <>
              {gridFill ? (
                <div className="mb-1 flex min-w-0 flex-wrap items-center justify-between gap-1">
                  <h4 className="min-w-0 truncate text-[0.6875rem] font-bold uppercase text-muted">{t('settings.grid.colorPicker.standard')}</h4>
                  <div className="ml-auto flex max-w-full shrink-0 overflow-hidden rounded-full border border-border" role="group" aria-label={t('grid.fillScopeLabel')}>
                    {([
                      { scope: 'cell', label: t('grid.fillScopeCell') },
                      { scope: 'column', label: t('grid.fillScopeColumn') },
                      { scope: 'header', label: t('grid.fillScopeHeader') },
                    ] as const).map(({ scope, label }) => (
                      <button
                        key={scope}
                        type="button"
                        disabled={disabled}
                        aria-pressed={gridFill.scope === scope}
                        onClick={() => gridFill.onScopeChange(scope)}
                        className={`h-6 min-w-0 truncate border-r border-border px-1.5 text-[0.6875rem] last:border-r-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus disabled:opacity-50 ${gridFill.scope === scope ? 'bg-accent/10 font-bold text-accent' : 'text-fg hover:bg-surface-hover'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : <h4 className="mb-1 text-[0.6875rem] font-bold uppercase text-muted">{t('settings.grid.colorPicker.standard')}</h4>}
              <div className="grid grid-cols-10 gap-0.5">
                {PALETTE.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => elegir(color)}
                    title={color}
                    className="h-5 w-5 rounded-full border border-border transition-transform hover:scale-110"
                    style={{
                      background: color,
                      outline: valor === color ? '2px solid var(--accent)' : undefined,
                      outlineOffset: 1,
                    }}
                  />
                ))}
              </div>

              {usados.length > 0 && (
                <>
                  <h4 className="mb-1 mt-3 text-[0.6875rem] font-bold uppercase text-muted">{t('settings.grid.colorPicker.custom')}</h4>
                  <div className="flex flex-wrap gap-1">
                    {usados.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => elegir(color)}
                        title={color}
                        className="h-5 w-5 rounded-full border border-border transition-transform hover:scale-110"
                        style={{
                          background: color,
                          outline: valor === color ? '2px solid var(--accent)' : undefined,
                          outlineOffset: 1,
                        }}
                      />
                    ))}
                  </div>
                </>
              )}

              <div className="mt-3 flex items-center gap-1">
                <input
                  readOnly
                  value={valor ?? ''}
                  placeholder="—"
                  className="h-8 w-full rounded-full border border-border bg-surface-2 px-2 text-xs font-mono uppercase text-fg"
                />
                <button
                  type="button"
                  title={t('settings.grid.colorPicker.copy')}
                  onClick={copiar}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border text-fg hover:border-accent"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="12" height="12" rx="2" />
                    <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
                  </svg>
                </button>
                <div className="relative shrink-0">
                  <button
                    type="button"
                    title={t('settings.grid.colorPicker.paste')}
                    onClick={() => void pegar()}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-fg hover:border-accent"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="8" y="2" width="8" height="4" rx="1" />
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                    </svg>
                  </button>
                  {errorPegar && (
                    <div className="absolute right-0 top-full z-50 mt-1 w-44 rounded-lg bg-danger px-2 py-1 text-[0.6875rem] text-white shadow-md">
                      {t('settings.grid.colorPicker.invalidHex')}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => setPersonalizando(true)}
                  className="flex-1 rounded-full border border-border py-1 text-xs font-semibold text-fg hover:bg-surface-3"
                >
                  {t('settings.grid.colorPicker.customize')}
                </button>
                <button
                  type="button"
                  onClick={() => elegir(null)}
                  className="flex-1 rounded-full py-1 text-xs text-muted hover:text-fg"
                >
                  {t('settings.grid.colorPicker.reset')}
                </button>
              </div>
              {gridFill && (
                <button
                  type="button"
                  disabled={disabled || gridFill.resetColumnDisabled}
                  onClick={() => {
                    gridFill.onResetColumn();
                    setAbierto(false);
                    setPersonalizando(false);
                  }}
                  className="mt-1 w-full min-w-0 truncate rounded-full border border-border px-2 py-1 text-xs text-fg hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50"
                >
                  {t('grid.resetAllColumn')}
                </button>
              )}
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <div
                  ref={cuadradoRef}
                  onPointerDown={onCuadradoPointer}
                  className="relative h-32 flex-1 touch-none cursor-crosshair rounded-lg border border-border"
                  style={{
                    backgroundImage:
                      'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,1)), linear-gradient(to right, #FF0000, #FFFF00, #00FF00, #00FFFF, #0000FF, #FF00FF, #FF0000)',
                  }}
                >
                  <span
                    className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
                    style={{ left: `${(hsvActual.h / 360) * 100}%`, top: `${100 - hsvActual.v}%` }}
                  />
                </div>
                <div
                  ref={barraRef}
                  onPointerDown={onBarraPointer}
                  className="relative h-32 w-4 touch-none cursor-pointer rounded-full border border-border"
                  style={{ backgroundImage: `linear-gradient(to bottom, #FFFFFF, ${colorPuro})` }}
                >
                  <span
                    className="pointer-events-none absolute left-0 right-0 h-1.5 -translate-y-1/2 border-y-2 border-white"
                    style={{ top: `${hsvActual.s}%` }}
                  />
                </div>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-1.5">
                {(
                  [
                    ['r', t('settings.grid.colorPicker.red')],
                    ['g', t('settings.grid.colorPicker.green')],
                    ['b', t('settings.grid.colorPicker.blue')],
                  ] as const
                ).map(([canal, etiqueta]) => (
                  <label key={canal} className="text-[0.625rem] font-bold uppercase text-muted">
                    {etiqueta}
                    <input
                      type="number"
                      min={0}
                      max={255}
                      value={Math.round(rgbActual[canal])}
                      onChange={(e) => aplicarRgb({ [canal]: clamp(Number(e.target.value) || 0, 0, 255) })}
                      className="mt-0.5 h-8 w-full rounded-full border border-border bg-surface-2 px-1.5 text-xs font-mono text-fg"
                    />
                  </label>
                ))}
              </div>

              <input
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                className="mt-2 h-9 w-full rounded-full border border-border bg-surface-2 px-2 text-sm font-mono uppercase text-fg"
              />

              <div className="mt-2 flex items-center gap-3 text-[0.6875rem] text-muted">
                <div className="flex flex-1 flex-col items-center gap-1">
                  <span className="h-6 w-full rounded-full border border-border" style={{ background: hex }} />
                  <span>{t('settings.grid.colorPicker.new')}</span>
                </div>
                <div className="flex flex-1 flex-col items-center gap-1">
                  <span
                    className="h-6 w-full rounded-full border border-border"
                    style={{
                      background: valor ?? 'transparent',
                      borderStyle: valor ? 'solid' : 'dashed',
                    }}
                  />
                  <span>{t('settings.grid.colorPicker.current')}</span>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setPersonalizando(false)}
                  className="flex-1 rounded-full border border-border py-1 text-xs font-semibold text-fg hover:bg-surface-3"
                >
                  {t('settings.grid.colorPicker.back')}
                </button>
                <button
                  type="button"
                  onClick={() => elegir(hex)}
                  className="flex-1 rounded-full bg-accent py-1 text-xs font-bold text-accent-fg hover:opacity-90"
                >
                  {t('settings.grid.colorPicker.apply')}
                </button>
              </div>
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

export default ColorPicker;
